// Loader for unified purchase history covering both Paddle (web) and
// Google Play (Android) orders. Joins paddle/play orders with
// credit_transactions on the matching `payment_ref`.
//
// Refund credit_transactions (reason starting with "refund:") are merged in
// as standalone negative-credit rows so users can see goodwill refunds.
//
// Currency: each row preserves the currency it was charged in. Android
// (Google Play) orders are typically in the user's local currency (INR for
// most Indian users) — so the UI must format using `row.currency`, not a
// hardcoded "$" / "USD".
import { supabase } from "@/integrations/supabase/client";

export type PurchaseStatus = "paid" | "pending" | "failed" | "refunded";
export type PurchaseKind = "purchase" | "refund";
export type PurchaseProvider = "Paddle" | "Google Play" | "System";

export interface PurchaseRow {
  id: string;
  createdAt: string;
  /** Positive for purchases, negative for refunds. */
  credits: number;
  /** Major units (e.g. dollars, rupees). 0 when unknown (legacy data). */
  amount: number;
  /** ISO 4217 currency code as charged by the provider (USD, INR, EUR, …). */
  currency: string;
  status: PurchaseStatus;
  provider: PurchaseProvider;
  /** Provider-specific reference (Paddle txn id or Play order id / token). */
  reference: string;
  kind: PurchaseKind;
  /** Paddle environment for invoice fetching. Undefined for Play. */
  environment?: "sandbox" | "live";
  /** Pack identifier when known (e.g. "value", "pro"). */
  packId?: string;
}

export interface CurrencyTotal {
  currency: string;
  amount: number;
  purchases: number;
}

export interface PurchaseSummary {
  /** Sum of all paid amounts in the dominant currency only. UI should prefer
   *  `byCurrency` when the user has multiple currencies. */
  totalSpent: number;
  /** Currency to label the headline summary with. Picks the most spent one. */
  summaryCurrency: string;
  /** Per-currency breakdown of paid purchases, sorted by amount desc. */
  byCurrency: CurrencyTotal[];
  totalCredits: number;
  totalRefundedCredits: number;
  totalPurchases: number;
}

/** Format a money amount using the row's own currency. */
export function formatPurchaseAmount(amount: number, currency: string): string {
  if (!(amount > 0)) return "—";
  try {
    return new Intl.NumberFormat(undefined, {
      style: "currency",
      currency,
      maximumFractionDigits: currency === "INR" || currency === "JPY" ? 0 : 2,
    }).format(amount);
  } catch {
    // Fallback if the browser doesn't recognize the currency code.
    return `${amount.toFixed(2)} ${currency}`;
  }
}

export async function loadPurchases(userId: string): Promise<{
  rows: PurchaseRow[];
  summary: PurchaseSummary;
}> {
  const [paddleOrders, playOrders, tx] = await Promise.all([
    supabase
      .from("paddle_orders")
      .select(
        "paddle_transaction_id, amount_cents, currency, status, credits, environment, pack_id, created_at, updated_at",
      )
      .eq("user_id", userId)
      .order("created_at", { ascending: false }),
    supabase
      .from("play_orders")
      .select(
        "id, play_order_id, play_purchase_token, play_product_id, amount_micros, currency, status, credits, environment, pack_id, created_at, updated_at",
      )
      .eq("user_id", userId)
      .order("created_at", { ascending: false }),
    supabase
      .from("credit_transactions")
      .select("id, created_at, delta, payment_ref, reason")
      .eq("user_id", userId)
      .order("created_at", { ascending: false })
      .limit(200),
  ]);

  if (paddleOrders.error) throw paddleOrders.error;
  if (playOrders.error) throw playOrders.error;
  if (tx.error) throw tx.error;

  // Index credit transactions by reference for matched amounts.
  const txByPaddleRef = new Map<string, (typeof tx.data)[number]>();
  const txByPlayRef = new Map<string, (typeof tx.data)[number]>();
  for (const t of tx.data ?? []) {
    if (t.payment_ref?.startsWith("paddle:")) {
      txByPaddleRef.set(t.payment_ref.slice("paddle:".length), t);
    } else if (t.payment_ref?.startsWith("play:")) {
      txByPlayRef.set(t.payment_ref.slice("play:".length), t);
    }
  }

  const rows: PurchaseRow[] = [];

  // 1. Paddle orders.
  for (const o of paddleOrders.data ?? []) {
    const matchedTx = txByPaddleRef.get(o.paddle_transaction_id);
    let status: PurchaseStatus = "pending";
    if (o.status === "paid" || matchedTx) status = "paid";
    else if (o.status === "failed" || o.status === "canceled") status = "failed";

    rows.push({
      id: `paddle-${o.paddle_transaction_id}`,
      createdAt: matchedTx?.created_at ?? o.created_at,
      credits: matchedTx ? Number(matchedTx.delta) : o.credits,
      amount: o.amount_cents / 100,
      currency: o.currency || "USD",
      status,
      provider: "Paddle",
      reference: o.paddle_transaction_id,
      kind: "purchase",
      environment: (o.environment as "sandbox" | "live") ?? "live",
      packId: o.pack_id,
    });
  }

  // 2. Google Play orders. amount_micros is price * 1,000,000 in the
  //    currency the user was charged in (typically INR for India).
  for (const o of playOrders.data ?? []) {
    // Webhook stores credit_transactions with payment_ref "play:<token>".
    const matchedTx =
      (o.play_purchase_token && txByPlayRef.get(o.play_purchase_token)) ||
      (o.play_order_id && txByPlayRef.get(o.play_order_id)) ||
      null;
    let status: PurchaseStatus = "pending";
    if (o.status === "paid" || matchedTx) status = "paid";
    else if (o.status === "failed" || o.status === "canceled") status = "failed";

    rows.push({
      id: `play-${o.id}`,
      createdAt: matchedTx?.created_at ?? o.created_at,
      credits: matchedTx ? Number(matchedTx.delta) : o.credits,
      amount: o.amount_micros ? Number(o.amount_micros) / 1_000_000 : 0,
      currency: o.currency || "INR",
      status,
      provider: "Google Play",
      reference: o.play_order_id || o.play_purchase_token || o.id,
      kind: "purchase",
      packId: o.pack_id,
    });
  }

  // 3. Refund credit_transactions surfaced as negative-credit rows.
  for (const t of tx.data ?? []) {
    if (!t.reason?.startsWith("refund:")) continue;
    rows.push({
      id: `refund-${t.id}`,
      createdAt: t.created_at,
      credits: Number(t.delta),
      amount: 0,
      currency: "USD",
      status: "refunded",
      // These are internal credit-backs written by our edge functions when a
      // generation fails (e.g. refund:tryon_failed). No payment provider was
      // involved — labeling them "Paddle" or "Google Play" would mislead
      // users (especially on Android where Paddle is never used).
      provider: "System",
      reference: t.reason.slice("refund:".length) || t.reason,
      kind: "refund",
    });
  }

  rows.sort((a, b) => +new Date(b.createdAt) - +new Date(a.createdAt));

  // Summary stats — group by currency, then pick the dominant one.
  const totals = new Map<string, { amount: number; purchases: number }>();
  let totalCredits = 0;
  let totalRefundedCredits = 0;
  let totalPurchases = 0;
  for (const r of rows) {
    if (r.kind === "purchase" && r.status === "paid" && r.amount > 0) {
      const cur = totals.get(r.currency) ?? { amount: 0, purchases: 0 };
      cur.amount += r.amount;
      cur.purchases += 1;
      totals.set(r.currency, cur);
      totalCredits += r.credits;
      totalPurchases += 1;
    } else if (r.kind === "purchase" && r.status === "paid") {
      // Paid purchase with unknown amount (legacy): still count credits.
      totalCredits += r.credits;
      totalPurchases += 1;
    } else if (r.kind === "refund") {
      totalRefundedCredits += Math.abs(r.credits);
    }
  }
  const byCurrency: CurrencyTotal[] = Array.from(totals, ([currency, v]) => ({
    currency,
    amount: v.amount,
    purchases: v.purchases,
  })).sort((a, b) => b.amount - a.amount);

  const summaryCurrency = byCurrency[0]?.currency ?? "USD";
  const totalSpent = byCurrency[0]?.amount ?? 0;

  return {
    rows,
    summary: {
      totalSpent,
      summaryCurrency,
      byCurrency,
      totalCredits,
      totalRefundedCredits,
      totalPurchases,
    },
  };
}

/** Mask a long transaction id for compact display: `txn_…ab12`. */
export function maskReference(ref: string): string {
  if (ref.length <= 10) return ref;
  return `${ref.slice(0, 4)}…${ref.slice(-4)}`;
}

/** Group rows by "Mon YYYY" header, preserving the input order. */
export function groupByMonth(rows: PurchaseRow[]): Array<{ label: string; rows: PurchaseRow[] }> {
  const map = new Map<string, PurchaseRow[]>();
  for (const r of rows) {
    const d = new Date(r.createdAt);
    const label = d.toLocaleString(undefined, { month: "long", year: "numeric" });
    const list = map.get(label) ?? [];
    list.push(r);
    map.set(label, list);
  }
  return Array.from(map, ([label, rows]) => ({ label, rows }));
}
